import type { ReactNode } from 'react';
import { AtSign, BadgeCheck, Hash, Mail, Phone, UserRound, type LucideIcon } from 'lucide-react';
import { ActiveStatusBadge } from '@/components/common/ActiveStatusBadge';
import { EmptyValue } from '@/components/common/EmptyValue';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { RAISED_CARD_CLASS } from '@/styles/raisedCard';
import { STATUS_COLOR_CLASS, type StatusColorTone } from '@/styles/statusColors';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';
import { getInitialsFromName } from '@/utils/getInitialsFromName';
import type { ProfileDisplayUser } from './types';

const COVER_GLOW_CLASS =
  'pointer-events-none absolute rounded-full bg-primary-foreground/15 blur-2xl';
const ROLE_SEPARATOR = ' · ';

interface IdentityChipProps {
  icon: LucideIcon;
  tone: StatusColorTone;
  children: ReactNode;
}

function IdentityChip({ icon: Icon, tone, children }: IdentityChipProps) {
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium',
        STATUS_COLOR_CLASS[tone],
      )}
    >
      <Icon className="size-3.5 shrink-0" aria-hidden />
      <span className="min-w-0 wrap-break-word">{children}</span>
    </span>
  );
}

interface IdentitySummaryProps {
  user: ProfileDisplayUser | undefined;
}

function IdentitySummary({ user }: IdentitySummaryProps) {
  if (!user) {
    return (
      <div className="flex flex-col items-center gap-2" aria-hidden>
        <Skeleton className="h-7 w-40" />
        <Skeleton className="h-4 w-48 max-w-full" />
        <Skeleton className="h-6 w-36 rounded-full" />
      </div>
    );
  }

  const roleLine = [user.designationName, user.departmentName].filter(Boolean).join(ROLE_SEPARATOR);

  return (
    <div className="flex min-w-0 flex-col items-center gap-2">
      <h2 className="text-xl font-semibold tracking-tight wrap-break-word">{user.fullName}</h2>
      <ActiveStatusBadge isActive={user.status === 'active'} />
      <p className="text-sm text-muted-foreground">{roleLine === '' ? <EmptyValue /> : roleLine}</p>
      <div className="flex flex-wrap justify-center gap-2">
        <IdentityChip icon={AtSign} tone="neutral">
          {user.username}
        </IdentityChip>
        {user.userTypeName && (
          <IdentityChip icon={BadgeCheck} tone="info">
            {user.userTypeName}
          </IdentityChip>
        )}
      </div>
    </div>
  );
}

interface ContactFactProps {
  label: string;
  value: string | null | undefined;
  icon: LucideIcon;
  isLoading: boolean;
}

function ContactFact({ label, value, icon: Icon, isLoading }: ContactFactProps) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <div
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-lg',
          ACCENT_MARK_CLASS,
        )}
      >
        <Icon className="size-4" aria-hidden />
      </div>
      <div className="min-w-0">
        <p className="text-2xs font-medium tracking-wider text-muted-foreground uppercase">
          {label}
        </p>
        {isLoading ? (
          <Skeleton className="mt-1 h-4 w-28" />
        ) : (
          <p className="text-sm font-medium wrap-break-word">{value ?? <EmptyValue />}</p>
        )}
      </div>
    </div>
  );
}

interface ProfileUserCardProps {
  user: ProfileDisplayUser | undefined;
}

export function ProfileUserCard({ user }: ProfileUserCardProps) {
  const isLoading = user === undefined;
  const initials = getInitialsFromName(user?.fullName);
  const contactFacts = [
    { label: 'Mobile', value: user?.mobile, icon: Phone },
    { label: 'Email', value: user?.email, icon: Mail },
    { label: 'Employee ID', value: user?.hrEmpId, icon: Hash },
  ];

  return (
    <Card className={cn('gap-0 py-0', RAISED_CARD_CLASS)}>
      <div
        aria-hidden
        className="relative h-24 overflow-hidden bg-linear-to-br from-primary to-primary-strong"
      >
        <span className={cn(COVER_GLOW_CLASS, '-top-20 -right-10 size-48')} />
        <span className={cn(COVER_GLOW_CLASS, '-bottom-24 -left-6 size-40')} />
      </div>
      <div className="flex flex-col items-center gap-3 px-5 pb-5 text-center">
        <Avatar className="-mt-12 size-24 shadow-lg ring-4 ring-card">
          <AvatarFallback className="bg-linear-to-br from-primary to-primary-strong text-2xl font-semibold text-primary-foreground">
            {initials === '' ? <UserRound className="size-10" aria-hidden /> : initials}
          </AvatarFallback>
        </Avatar>
        <IdentitySummary user={user} />
      </div>
      <div className="flex flex-col gap-4 border-t border-border/60 p-5">
        {contactFacts.map((fact) => (
          <ContactFact key={fact.label} {...fact} isLoading={isLoading} />
        ))}
      </div>
    </Card>
  );
}
